import React, { useState, useEffect, ComponentType } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { intakeApi } from '../../api/api';
import { IntakeFormData, User, StepProps, Products, Division } from '../../types';
import { isValidEmail } from '../../utils/formHelpers';
import LoadingSpinner from '../../components/LoadingSpinner';
import LeagueInfoStep from './steps/LeagueInfoStep';
import StructureStep from './steps/StructureStep';
import ProductsStep from './steps/ProductsStep';
import ReviewStep from './steps/ReviewStep';
import './IntakeForm.css';

interface Step {
  id: string;
  title: string;
  component: ComponentType<StepProps>;
}

const STEPS: Step[] = [
  { id: 'info', title: 'League Info', component: LeagueInfoStep },
  { id: 'structure', title: 'Teams', component: StructureStep },
  { id: 'products', title: 'Products', component: ProductsStep },
  { id: 'review', title: 'Review', component: ReviewStep },
];

const getInitialProducts = (): Products => ({
  jerseys: {
    enabled: true,
    type: 'full-button',
    notes: '',
    teamSizes: {},
  },
  pants: {
    enabled: true,
    type: 'standard',
    notes: '',
    teamSizes: {},
  },
  belts: {
    enabled: true,
    notes: '',
    teamSizes: {},
  },
  socks: {
    enabled: true,
    type: 'crew',
    notes: '',
    teamSizes: {},
  },
  caps: {
    enabled: true,
    type: 'fitted',
    notes: '',
    teamSizes: {},
  },
});

const getInitialDivisions = (): Division[] => [
  {
    name: '',
    teams: [{ name: '', color: '', sponsorName: '' }],
  },
];

const getInitialFormData = (user: User | null): IntakeFormData => ({
  leagueName: user?.leagueName || '',
  contactName: user?.contactName || '',
  contactEmail: user?.email || '',
  contactPhone: user?.phone || '',
  requestedDate: '',
  season: '',
  notes: '',
  divisions: getInitialDivisions(),
  products: getInitialProducts(),
});

const IntakeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<IntakeFormData>(getInitialFormData(user));
  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!id;

  // Load existing submission if editing
  useEffect(() => {
    if (id) {
      const loadSubmission = async (): Promise<void> => {
        try {
          const response = await intakeApi.get(id);
          const sub = response.data.submission;

          if (sub.status !== 'submitted') {
            navigate('/dashboard', {
              state: { error: 'This submission can no longer be edited' }
            });
            return;
          }

          // Map submission data to form data
          setFormData({
            leagueName: sub.league_name,
            contactName: sub.contact_name,
            contactEmail: sub.contact_email,
            contactPhone: sub.contact_phone || '',
            requestedDate: sub.requested_date ? sub.requested_date.slice(0, 10) : '',
            season: sub.season || '',
            notes: sub.notes || '',
            divisions: sub.order_details?.divisions || getInitialDivisions(),
            products: sub.order_details?.products || getInitialProducts(),
          });
        } catch {
          setError('Failed to load submission');
        }
        setLoading(false);
      };
      loadSubmission();
    }
  }, [id, navigate]);

  const updateFormData = (updates: Partial<IntakeFormData>): void => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const calculateTotals = (): { totalTeams: number; totalItems: number } => {
    let totalTeams = 0;
    let totalItems = 0;

    formData.divisions.forEach(division => {
      totalTeams += division.teams.length;
    });

    // Helper to sum quantities from a size object
    const sumSizes = (sizes: Record<string, unknown>): number => {
      return Object.values(sizes).reduce((sum: number, val) => {
        if (typeof val === 'number') {
          return sum + (val || 0);
        }
        return sum;
      }, 0);
    };

    // Calculate actual item count from size quantities
    const productKeys: (keyof Products)[] = ['jerseys', 'pants', 'belts', 'socks', 'caps'];
    productKeys.forEach(key => {
      const product = formData.products[key];
      if (product.enabled && product.teamSizes) {
        Object.values(product.teamSizes).forEach(teamSizes => {
          // Check if this is a nested structure (jerseys/pants with youth/adult/coach)
          if (teamSizes && typeof teamSizes === 'object') {
            const firstValue = Object.values(teamSizes)[0];
            if (firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
              // Nested structure: { youth: {...}, adult: {...}, coach: {...} }
              Object.values(teamSizes).forEach(category => {
                if (category && typeof category === 'object') {
                  totalItems += sumSizes(category as Record<string, unknown>);
                }
              });
            } else {
              // Flat structure: { size1: qty, size2: qty }
              totalItems += sumSizes(teamSizes as Record<string, unknown>);
            }
          }
        });
      }
    });

    return { totalTeams, totalItems };
  };

  // Validation for each step
  const validateStep = (stepIndex: number): string | null => {
    switch (stepIndex) {
      case 0: // League Info
        if (!formData.leagueName.trim()) return 'League name is required';
        if (!formData.contactName.trim()) return 'Contact name is required';
        if (!formData.contactEmail.trim()) return 'Email is required';
        if (!isValidEmail(formData.contactEmail)) return 'Please enter a valid email address';
        return null;

      case 1: // Structure
        if (formData.divisions.length === 0) return 'Add at least one division';
        for (let i = 0; i < formData.divisions.length; i++) {
          const div = formData.divisions[i];
          if (!div.name.trim()) return `Division ${i + 1} needs a name`;
          if (div.teams.length === 0) return `Division "${div.name || i + 1}" needs at least one team`;
          for (let j = 0; j < div.teams.length; j++) {
            const team = div.teams[j];
            if (!team.name.trim()) return `Team ${j + 1} in "${div.name || `Division ${i + 1}`}" needs a name`;
          }
        }
        return null;

      case 2: // Products
        const { totalItems } = calculateTotals();
        if (totalItems === 0) return 'Add at least one product with sizes';
        return null;

      default:
        return null;
    }
  };

  const handleNext = (): void => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = (): void => {
    setError('');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setError('');
    setSubmitting(true);

    const { totalTeams, totalItems } = calculateTotals();

    const submissionData = {
      leagueName: formData.leagueName,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      requestedDate: formData.requestedDate || null,
      season: formData.season,
      notes: formData.notes,
      orderDetails: {
        divisions: formData.divisions,
        products: formData.products,
      },
      totalTeams,
      totalItems,
      estimatedTotal: 0, // Staff will add pricing
    };

    try {
      if (isEditing) {
        await intakeApi.update(id!, submissionData);
        navigate('/dashboard', {
          state: { message: 'Submission updated successfully' }
        });
      } else {
        const response = await intakeApi.create(submissionData);
        navigate('/dashboard', {
          state: {
            message: `Submission ${response.data.submission.submissionNumber} created successfully`
          }
        });
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to submit form');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="intake-form">
        <div className="loading-container">
          <LoadingSpinner text="Loading submission..." />
        </div>
      </div>
    );
  }

  const StepComponent = STEPS[currentStep].component;

  return (
    <div className="intake-form">
      <div className="form-header">
        <h1 className="page-title">
          {isEditing ? 'Edit Order Request' : 'New Order Request'}
        </h1>
        <p className="page-subtitle">
          Submit your league uniform order details for review
        </p>
      </div>

      <div className="progress-bar">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`progress-step ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
          >
            <div className="step-number">{index + 1}</div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="form-content">
        <StepComponent
          formData={formData}
          updateFormData={updateFormData}
          isEditing={isEditing}
        />
      </div>

      <div className="form-navigation">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={currentStep === 0 ? () => navigate('/dashboard') : handleBack}
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>

        {currentStep < STEPS.length - 1 ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <LoadingSpinner size="small" />
            ) : (
              isEditing ? 'Update Submission' : 'Submit Request'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default IntakeForm;
